import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Search, Menu, X } from 'lucide-react';
import { useSearch } from '../context/SearchContext';
import { motion, AnimatePresence } from 'framer-motion';

export default function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { searchQuery, setSearchQuery } = useSearch();
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate('/shop');
    }
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Shop', path: '/shop' },
    { name: 'Categories', path: '/categories' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav className="bg-white/90 backdrop-blur-md border-b border-gray-100 shadow-sm sticky top-0 z-50 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">

          <div className="flex-shrink-0 flex items-center">
            <NavLink to="/" className="cursor-target flex items-center gap-2.5">
              <img src="/logo.jpeg" alt="Saim Autos" width="48" height="48" className="h-10 sm:h-12 object-contain rounded-xl" />
              <span className="font-heading text-lg sm:text-xl font-bold text-gray-900 tracking-tight">Saim Autos</span>
            </NavLink>
          </div>

          <div className="hidden md:flex space-x-8">
            {navLinks.map((link) => (
              <NavLink
                key={link.name}
                to={link.path}
                className={({ isActive }) =>
                  `cursor-target px-2 py-1 text-sm font-medium tracking-wide transition-colors hover:text-red-600 ${isActive ? 'text-red-600 font-semibold' : 'text-gray-700'
                  }`
                }
              >
                {link.name}
              </NavLink>
            ))}
          </div>

          <div className="hidden md:flex items-center space-x-6">
            <form onSubmit={handleSearch} className="relative flex items-center">
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-48 lg:w-64 px-4 py-2 rounded-full bg-gray-50 border border-gray-200 text-sm outline-none focus:border-red-300 focus:ring-2 focus:ring-red-100 transition-all"
              />
              <button type="submit" aria-label="Submit search" className="cursor-target absolute right-3 text-gray-400 hover:text-red-600 transition-colors">
                <Search size={16} strokeWidth={2.5} />
              </button>
            </form>
          </div>

          <div className="flex items-center md:hidden space-x-4">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              className="text-gray-700 outline-none hover:text-red-600 transition-colors"
            >
              {isMobileMenuOpen ? <X size={26} strokeWidth={2} /> : <Menu size={26} strokeWidth={2} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, y: -8, scaleY: 0.96 }}
            animate={{ opacity: 1, y: 0, scaleY: 1 }}
            exit={{ opacity: 0, y: -8, scaleY: 0.96 }}
            transition={{ duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] }}
            style={{ transformOrigin: 'top' }}
            className="md:hidden bg-white border-t border-gray-100 px-4 py-4 absolute w-full shadow-lg rounded-b-2xl overflow-hidden"
          >
            <form onSubmit={handleSearch} className="relative mb-5 flex">
              <input
                type="text"
                placeholder="Search parts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2.5 rounded-l-xl border border-gray-200 text-sm outline-none bg-gray-50 focus:border-red-300 transition-colors"
              />
              <button type="submit" aria-label="Submit mobile search" className="cursor-target px-4 bg-red-600 text-white rounded-r-xl hover:bg-red-700 transition-colors flex items-center justify-center">
                <Search size={18} strokeWidth={2.5} />
              </button>
            </form>
            <div className="flex flex-col space-y-1">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.055 + 0.08, duration: 0.22, ease: 'easeOut' }}
                >
                  <NavLink
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `cursor-target block px-4 py-3 text-base font-medium rounded-xl transition-all ${isActive ? 'bg-red-50 text-red-600' : 'text-gray-700 hover:bg-gray-50 hover:text-red-500'
                      }`
                    }
                  >
                    {link.name}
                  </NavLink>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
